import { Component, inject, Input, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormGroupDirective, NgForm, UntypedFormArray, Validators } from '@angular/forms';
import { CameraService } from '../../../shared/image-uploader/camera/camera.service';
import moment from 'moment';
import { debounceTime, first, map } from 'rxjs/operators';

import { AdditionalsService } from '../../services/additionals.service';
import { AdminService } from '../../services/admin.service';
import { RequirementsService } from '../../services/requirements.service';
import { getDownloadURL } from 'firebase/storage';
import { LocationService } from '../../services/location.service';
import { HttpResponse } from '@angular/common/http';
import { RoleService } from '../../../role/role.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { ErrorStateMatcher, ShowOnDirtyErrorStateMatcher } from '@angular/material/core';


/** Error when invalid control is dirty, touched, or submitted. */
export class MyErrorStateMatcher implements ErrorStateMatcher {
  isErrorState(control: FormControl | null, form: FormGroupDirective | NgForm | null): boolean {
    return !!(control && control.invalid);
  }
}

@Component({
    selector: 'app-admin-booking',
    templateUrl: './admin-booking.component.html',
    styleUrls: ['./admin-booking.component.scss', '../admin-main.common.scss'],
    standalone: false,
    providers: [
      {provide: ErrorStateMatcher, useClass: ShowOnDirtyErrorStateMatcher}
    ],
})
export class AdminBookingComponent implements OnInit {
  @Input() hideAdditionals = false;

  additionals:any = [];

  days = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];

  email: string;

  private readonly fb = inject(FormBuilder);

  workingDays = this.fb.group({
    work_days: new UntypedFormArray([]),
  });

  bookingDays = this.fb.group({
    days: new UntypedFormArray([]),
  });

  workHours = this.fb.group({
    time_interval: this.fb.control(null),
    max_bookings: this.fb.control(null),
    max_bookings_per_day: this.fb.control(null),
    mailing: this.fb.group({
      address: null,
      phone: null,
      webpage: null,
      facebook: null,
      instagram: null,
      email: null,
    }),
  });

  locations = [];

  locationId = null;

  now = moment();

  form = this.fb.group({
    name: this.fb.control('', Validators.required),
    price: this.fb.control(null),
    hide: this.fb.control(null),
    show_client_booking: this.fb.control(null),
  });

  formBooking = this.fb.group({
    preferences: this.fb.group({
      booking_role_ids: [],
      booking_type: 'capacity',
      booking_format: 'appointment',
      temp_identifier: false,

      booking_mail: this.fb.group({
        enable_booked_mail: this.fb.control(true, { nonNullable: true }),
        enable_confirmed_mail: this.fb.control(true, { nonNullable: true }),
        banner_url: this.fb.control(null),
        banner_style: this.fb.control(null),
        text: this.fb.control(null),
        confirm_text: this.fb.control(null),
      }),
      booking_addon: this.fb.group({
        allowed_domains: this.fb.control(null),
        background_color: this.fb.control(null),
      }),
    }),
  });

  subscriptions = [];

  booking_formats = [{
    id: 'appointment',
    name: 'Cita',
  }, {
    id: 'quote',
    name: 'Cotización',
  }];

  booking_types = [{
    id: 'capacity',
    name: 'Según capacidad',
  }, {
    id: 'professional',
    name: 'Según Profesionales',
  }, {
    id: 'stock',
    name: 'Según Stock',
  }];

  matcher = new MyErrorStateMatcher();

  private readonly locationService = inject(LocationService);

  public readonly roleService = inject(RoleService);

  roles = toSignal(this.roleService.simple_index().pipe(map(response => response.body)), { initialValue: [] });


  constructor(
    public additionalsService: AdditionalsService,
    public requirementsService: RequirementsService,
    public companyService: AdminService,
    private cameraService: CameraService,
  ) {
    for (let i = 0; i < 7; i += 1) {
      const dayForm = this.fb.group({
        enabled: this.fb.control(false),
        start_time: this.fb.control('08:00', [Validators.required, Validators.pattern(/^(0[0-9]|1[0-9]|2[0-3]):[0-5][0-9]$/)]),
        end_time: this.fb.control('18:00', [Validators.required, Validators.pattern(/^(0[0-9]|1[0-9]|2[0-3]):[0-5][0-9]$/)]),
        max_hours: this.fb.control(''),
      });
      const workdayForm = this.fb.group({
        enabled: this.fb.control(false),
        start_time: this.fb.control('08:00', [Validators.required, Validators.pattern(/^(0[0-9]|1[0-9]|2[0-3]):[0-5][0-9]$/)]),
        end_time: this.fb.control('18:00', [Validators.required, Validators.pattern(/^(0[0-9]|1[0-9]|2[0-3]):[0-5][0-9]$/)]),
        max_hours: this.fb.control(''),
      });
      (<UntypedFormArray> this.workingDays.controls.work_days).push(workdayForm);
      (<UntypedFormArray> this.bookingDays.controls.days).push(dayForm);
    }
  }

  ngOnInit() {
    this.locationService.all().subscribe((response) => {
      this.locations = response.body;
      if (this.locations.length > 0) {
        this.locationId = this.locations[0].id;
        this.setLocation(this.locationId);
      }
    });

    this.companyService.local$.subscribe((company) => {
      this.formBooking.patchValue(company);
      this.locations = company.locations;
      this.email = company.email_hash;
    });
    this.formBooking.valueChanges.pipe(debounceTime(500)).subscribe((value) => {
      this.companyService.updateCompany(this.formBooking.value).pipe(first()).subscribe();
    });
  }

  formSubscribe() {
    this.subscriptions = [];
    this.subscriptions.push(this.workHours.valueChanges.pipe(debounceTime(1000)).subscribe((value) => {
      if (!this.workHours.valid) {
        return;
      }
      this.locationService.update(this.locationId, { preferences: value }).toPromise().then((response) => {
        this.updateLocation(response.body);
      });
    }));
    this.subscriptions.push(this.workingDays.valueChanges.pipe(debounceTime(1000)).subscribe((value) => {
      if (!this.workingDays.valid) {
        return;
      }
      this.locationService.update(this.locationId, { preferences: value }).toPromise().then((response) => {
        this.updateLocation(response.body);
      });
    }));
    this.subscriptions.push(this.bookingDays.valueChanges.pipe(debounceTime(1000)).subscribe((value) => {
      if (!this.bookingDays.valid) {
        return;
      }
      this.locationService.update(this.locationId, { preferences: value }).subscribe((response) => {
        this.updateLocation(response.body);
      });
    }));
  }

  setLocation(id) {
    this.locationId = id;
    this.patchForms(this.getLocation(id));
  }

  updateLocation(value) {
    const index = this.locations.findIndex((l) => l.id === this.locationId);
    this.locations[index] = value;
    this.patchForms(value);
  }

  patchForms(value) {
    this.ngOnDestroy();
    this.workingDays.patchValue(value.preferences);
    this.bookingDays.patchValue(value.preferences);
    this.workHours.patchValue(value.preferences);
    this.formSubscribe();
  }

  getLocation(id) {
    return this.locations.find((l) => l.id === id);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  onFileChange(event:any, form:FormGroup) {
    const uploadTask = this.cameraService.saveImage(event.target?.files[0]);
    uploadTask.then((snapshot) => {
      getDownloadURL(snapshot.ref).then((url) => {
        form.patchValue({
          preferences: {
            booking_mail: {
              banner_url: url,
            },
          },
        });
      });
    });
  }
}
